import React from 'react';
import PropTypes from 'prop-types';
import { Box, Link, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { Info as InfoIcon, OpenInNew as OpenInNewIcon, Warning } from '@material-ui/icons';

import { formatNumber } from 'src/utils/formatters';
import { getColorByConcernLevel } from 'src/utils/aspectCategoriesUtils';


const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    backgroundColor: theme.palette.common.gray100,
  },
  concernTitle: {
    display: 'flex',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
  },
  concernLabel: {
    borderRadius: theme.shape.borderRadius,
    height: theme.spacing(3.5),
    minWidth: theme.spacing(18),
    maxWidth: theme.spacing(18),
    fontWeight: 500,
    marginLeft: theme.spacing(3),
    borderWidth: 1,
    borderStyle: 'solid',
  },

  concernVeryLow: {
    borderColor: theme.palette.success.main,
    color: theme.palette.success.main,
    backgroundColor: '#d1e7dd',
  },
  concernLow: {
    borderColor: theme.palette.flora.primary.main,
    color: theme.palette.flora.primary.main,
    backgroundColor: '#eaf5e1',
  },
  concernMiddle: {
    borderColor: theme.palette.fauna.secondary.main,
    color: theme.palette.fauna.secondary.main,
    backgroundColor: '#fef1db',
  },
  concernHigh: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    backgroundColor: '#fcdada',
  },
  concernVeryHigh: {
    color: theme.palette.error.dark,
    borderColor: theme.palette.error.dark,
    backgroundColor: '#fcdada',
  },
  concernValue: {
    fontSize: '1.125rem',
    fontWeight: 500,
  },
  group: {
    padding: '4px',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
  partialContainer: {
    opacity: 0.6,
  },
  warningIcon: {
    fontSize: '1rem',
    color: theme.palette.warning.main,
    marginLeft: theme.spacing(0.5),
  },
  infoIcon: {
    fontSize: '1rem',
    color: theme.palette.common.gray600,
    marginLeft: theme.spacing(0.5),
    cursor: 'pointer',
  },
  openInNewIcon: {
    fontSize: '0.75rem',
    verticalAlign: 'middle',
    marginRight: 2,
  },
  sourceLink: {
    alignSelf: 'flex-end',
    display: 'inline-flex',
    alignItems: 'center',
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  tooltipWide: {
    maxWidth: 480,
    backgroundColor: 'rgba(97, 97, 97, 0.96)',
  },
}));

const ConcernSummary = ({ title, tooltip, url, children, isPartial = false, value = null }) => {
  const classes = useStyles();
  return (
    <Box className={`${classes.container} ${isPartial ? classes.partialContainer : ''}`}>
      <Box alignSelf="flex-start" mt={1}>
        <Box display="flex" alignItems="center">
          <Typography className={classes.concernTitle} variant="body2">
            {title}
          </Typography>
          {tooltip && (
            <Tooltip
              interactive
              arrow
              placement="top"
              classes={{ tooltip: classes.tooltipWide }}
              title={
                <Typography variant="caption" component="div" className={classes.tooltipContent}>
                  {tooltip}
                  {url && (
                    <Link
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      color="inherit"
                      className={classes.sourceLink}
                    >
                      <OpenInNewIcon className={classes.openInNewIcon} />
                      Ver fuente
                    </Link>
                  )}
                </Typography>
              }
            >
              <InfoIcon className={classes.infoIcon} />
            </Tooltip>
          )}
          {isPartial && (
            <Warning className={classes.warningIcon} />
          )}
        </Box>
      </Box>
      <Box width="100%" display="flex" alignSelf="flex-start" alignItems="center" pt={1} pb={2} justifyContent="space-between">
        <Typography variant="body2">
          {children}
        </Typography>
        <Box display="flex" alignItems="center" className={classes.group}>
          <Box ml={1} mr={1.5}>
            <Typography className={classes.concernValue} style={{ color: getColorByConcernLevel(value) }} variant="h6" fontWeight="bold">
              {value !== null ? formatNumber(value, 2) : 'N/D'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>

  );
};

ConcernSummary.propTypes = {
  title: PropTypes.string,
  tooltip: PropTypes.node,
  url: PropTypes.string,
  children: PropTypes.node,
  isPartial: PropTypes.bool,
  value: PropTypes.number,
};


export {
  ConcernSummary,
};
